<template>
  <div class="admins-list">
    <div class="admins-list__filters-wrp">
      <div class="admins-list__filters app-list-filters">
        <a-row type="flex">
          <a-col span="20">
            <template>
              <custom-input
                v-model="search"
                :placeholder="'admins-list.lbl-search' | globalize"
              />
            </template>
          </a-col>
          <a-col span="4">
            <permission-wrapper
              skip-alert
              page-name="user"
              resource-name="user"
              action-name="update_foreign">
              <button
                v-ripple
                class="app__button-raised"
                @click="redirectToAdminManagerForm"
              >
                {{ 'admins-list.lbl-add-admin' | globalize }}
              </button>
            </permission-wrapper>
          </a-col>
        </a-row>
      </div>
    </div>

    <div class="admins-list__list-wrp">
      <div class="app-list">
        <div class="app-list__header">
          <span class="app-list__cell app-list__cell--left">
            {{ "admins-list.title-admin-id" | globalize }}
          </span>
          <span class="app-list__cell app-list__cell--center">
            {{ "admins-list.title-admin-role" | globalize }}
          </span>
          <span class="app-list__cell app-list__cell--right">
            {{ "admins-list.title-email" | globalize }}
          </span>
        </div>

        <template v-if="isLoaded && adminsList.length && !isFailed">
          <button
            v-ripple
            :disabled="userDetailsDisabled"
            class="app-list__li"
            v-for="item in adminsList"
            :key="`admin-${item.id}`"
            @click="redirectToAdminDetails(item.id)"
          >
            <span
              class="app-list__cell app-list__cell--left"
              :title="item.id"
            >
              {{ item.id }}
            </span>

            <span
              class="app-list__cell app-list__cell--center"
              :title="item.role"
            >
              {{ item.role | formatUserRole }}
            </span>

            <span
              class="app-list__cell app-list__cell--right"
              :title="item.email"
            >
              {{ item.email }}
            </span>
          </button>
        </template>

        <template v-else>
          <div class="app-list__li-like">
            <template v-if="isLoaded && !adminsList.length">
              <p>
                {{ "admins-list.nothing-here-yet" | globalize }}
              </p>
            </template>

            <template v-else-if="isFailed">
              <p>
                {{ "admins-list.default-error-message" | globalize }}
              </p>
            </template>

            <template v-else>
              <p>
                {{ "admins-list.loading-message" | globalize }}
              </p>
            </template>
          </div>
        </template>
      </div>

      <div class="app__more-btn-wrp">
        <collection-loader
          class="app__more-btn-wrp"
          :first-page-loader="getAdmins"
          @first-page-load="setList"
          @next-page-load="concatList"
          ref="collectionLoaderBtn"
        />
      </div>
    </div>
  </div>
</template>

<script>
import CollectionLoader from '@/vue/common/CollectionLoader'
import CustomInput from '@/vue/fields/CustomInput'
import ARow from 'ant-design-vue/lib/row'
import ACol from 'ant-design-vue/lib/col'
import { ErrorHandler } from '@/js/helpers/error-handler'
import { UserRecord } from '@/js/records/entities/user.record'
import { vueRoutes } from '@/vue-router/routes'
import { clearObject } from '@/js/helpers/clear-object'
import { api } from '@/api'
import { addParamsToLocation, queryToFilters } from '@/js/helpers/router-query'
import PermissionMixin from '@/vue/mixins/permission.mixin'
import PermissionWrapper from '@/vue/common/permission-wrapper'

const ROLE_TYPE_ADMIN = 'admin'

const defaultFilters = {
  adminId: '',
  role: '',
  email: '',
}

export default {
  name: 'admins-list',
  components: {
    CustomInput,
    CollectionLoader,
    ARow,
    ACol,
    PermissionWrapper,
  },
  mixins: [ PermissionMixin ],
  data () {
    return {
      search: '',
      isLoaded: false,
      isFailed: false,
      isPending: false,
      adminsList: [],
      isFilterAllOver: true,
      filters: defaultFilters,
    }
  },

  computed: {
    userDetailsDisabled () {
      const result = this.isForbidden('users', 'user', 'read')
      return result
    },
  },

  watch: {
    filters: {
      handler () {
        this.isPending = true
        if (this.isLoaded) {
          this.reloadList()
        }
      },
      deep: true,
    },
    search: {
      handler () {
        this.chooseFilter()
      },
    },
    isLoaded: {
      handler (value) {
        if (value && this.isPending) {
          this.reloadList()
          this.isPending = false
        }
      },
    },
    '$route' () {
      const filters = queryToFilters(defaultFilters)
      this.filters = filters
      this.search = this.oneOf(this.filters)
      return this.getAdmins()
    },
  },

  async created () {
    this.filters = queryToFilters(defaultFilters)
    this.search = this.oneOf(this.filters)
    await this.getAdmins()
  },
  methods: {
    async getAdmins () {
      this.isLoaded = false
      this.isFailed = false

      let response = {}
      try {
        const query = {
          filter: clearObject({
            user_id: this.filters.adminId,
            email: this.filters.email,
            role: ROLE_TYPE_ADMIN,
          }),
        }

        response = await api.getWithSignature('/users', query)
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
        this.isFailed = true
      }

      this.isLoaded = true
      return response
    },

    setList (data) {
      this.adminsList = data.map(i => new UserRecord(i))
    },

    concatList (data) {
      this.adminsList = this.adminsList.concat(data.map(i => new UserRecord(i)))
    },

    reloadList () {
      addParamsToLocation(this.$route, this.filters)
      this.$refs.collectionLoaderBtn.loadFirstPage()
    },

    toggleFilterFields () {
      this.isFilterAllOver = !this.isFilterAllOver
    },

    clearFilters () {
      Object.getOwnPropertyNames(this.filters).forEach(prop => {
        this.filters[prop] = ''
      })
    },

    redirectToAdminDetails (id) {
      this.$router.push({
        name: vueRoutes.adminDetails.name,
        params: { id },
      })
    },

    redirectToAdminManagerForm () {
      this.$router.push({ name: vueRoutes.adminsManagerAdd.name })
    },

    chooseFilter () {
      this.clearFilters()
      if (this.search.includes('@')) {
        this.filters.email = this.search
      } else if (this.search.match(/^[0-9]+$/) != null && this.search[0] !== '0') {
        this.filters.adminId = this.search
      } else {
        this.clearFilters()
      }
    },

    oneOf (filters) {
      let result = ''

      Object.keys(filters).forEach(k => {
        if (filters[k]) {
          result = filters[k]
        }
      })
      return result
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@scss/variables';
@import '~ant-design-vue/lib/grid/style/index.css';

.admins-list__list-wrp {
  margin-top: 4rem;
}

.app-list-filters {
  display: inherit;
}
</style>
